import { DestroyRef, WritableSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Subject,
  EMPTY,
  debounceTime,
  switchMap,
  finalize,
  catchError,
} from 'rxjs';
import { SpaceService } from '../services';
import { UnifiedMember, SearchMode } from '../models';
import { USER_SEARCH_MIN_CHARS, SEARCH_DEBOUNCE_MS } from './user-search.util';
import { SpaceAssignee } from '../models/space.model';

export interface AssigneesSearchConfig {
  mode: () => SearchMode;
  spaceId: () => number | undefined;
  folderId: () => number | undefined;
  tableId: () => number | undefined;
}

export interface AssigneesSearchStore {
  readonly results: WritableSignal<UnifiedMember[]>;
  readonly loading: WritableSignal<boolean>;
  readonly loadingMore: WritableSignal<boolean>;
  readonly meta: WritableSignal<{
    current_page: number;
    last_page: number;
  } | null>;
  search(query: string): void;
  loadMore(query: string): void;
  reset(): void;
}

function toUnifiedMember(a: SpaceAssignee): UnifiedMember {
  return {
    id: a.id,
    name: a.name,
    email: a.email ?? '',
    member_type: a.type as 'user' | 'group',
    members_count: a.members_count,
    is_selected: a.is_selected,
  };
}

export function createAssigneesSearch(
  service: SpaceService,
  destroyRef: DestroyRef,
  config: AssigneesSearchConfig
): AssigneesSearchStore {
  const subject = new Subject<string>();
  const results = signal<UnifiedMember[]>([]);
  const loading = signal(false);
  const loadingMore = signal(false);
  const meta = signal<{ current_page: number; last_page: number } | null>(null);
  let lastQuery: string | null = null;

  function getRequest$(query: string | undefined, page: number) {
    const mode = config.mode();
    if (mode === SearchMode.FolderAssignees) {
      return service.searchFolderAssignees(query, page, config.folderId());
    }
    if (mode === SearchMode.TableAssignees) {
      return service.searchTableAssignees(query, page, config.tableId());
    }
    return service.searchAssignees(query, page, config.spaceId());
  }

  subject
    .pipe(
      debounceTime(SEARCH_DEBOUNCE_MS),
      switchMap(query => {
        loading.set(true);
        results.set([]);
        meta.set(null);
        const effectiveQuery =
          query.length >= USER_SEARCH_MIN_CHARS ? query : undefined;
        return getRequest$(effectiveQuery, 1).pipe(
          finalize(() => loading.set(false)),
          catchError(() => EMPTY)
        );
      }),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe(response => {
      results.set(response.data.map(toUnifiedMember));
      meta.set({
        current_page: response.meta.current_page,
        last_page: response.meta.last_page,
      });
    });

  return {
    results,
    loading,
    loadingMore,
    meta,

    search(query: string): void {
      const effectiveQuery = query.length >= USER_SEARCH_MIN_CHARS ? query : '';
      if (effectiveQuery === lastQuery && results().length > 0) return;
      lastQuery = effectiveQuery;
      loading.set(true);
      subject.next(query);
    },

    loadMore(query: string): void {
      const m = meta();
      if (!m || m.current_page >= m.last_page || loadingMore()) return;
      const nextPage = m.current_page + 1;
      const effectiveQuery =
        query.length >= USER_SEARCH_MIN_CHARS ? query : undefined;
      loadingMore.set(true);
      getRequest$(effectiveQuery, nextPage)
        .pipe(
          finalize(() => loadingMore.set(false)),
          catchError(() => EMPTY),
          takeUntilDestroyed(destroyRef)
        )
        .subscribe(response => {
          results.update(prev => [
            ...prev,
            ...response.data.map(toUnifiedMember),
          ]);
          meta.set({
            current_page: response.meta.current_page,
            last_page: response.meta.last_page,
          });
        });
    },

    reset(): void {
      results.set([]);
      loading.set(false);
      loadingMore.set(false);
      meta.set(null);
      lastQuery = null;
    },
  };
}
